import React, { useState, useEffect } from "react";
import ImageUploader from "./ImageUploader";

const GroupUploaderModal = ({
  isOpen,
  onClose,
  members,
  onUpdateMember,
  onUploadMemberImage,
  globalCompositionSnippet
}) => {
  const [error, setError] = useState(null);

  // Clear local error when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);
  if (!isOpen) return null;

  const handleCloseOrConfirm = () => {
    // Validate if any member has an uploaded image but blank label
    const invalidMembers = members.filter(
      (m) => m.image !== null && (!m.label || m.label.trim() === "")
    );
    if (invalidMembers.length > 0) {
      const firstInvalidIndex = members.findIndex(
        (m) => m.image !== null && (!m.label || m.label.trim() === "")
      );
      setError(
        `Label/Identifier is mandatory and cannot be blank for uploaded members (please fill in Source 0${
          firstInvalidIndex + 1
        }).`
      );
      return;
    }
    setError(null);
    onClose();
  };

  return (
    // <div className="fixed inset-0 z-[60] flex items-center justify-center md:p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-300">
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center md:p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-300"
      style={{ zIndex: "100" }}
    >
      <div className="glass w-full h-full md:max-w-6xl md:max-h-[95vh] md:rounded-[3rem] overflow-hidden flex flex-col border-indigo-500/20 shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 md:px-10 md:py-8 border-b border-white/5 flex justify-between items-center bg-slate-900/40">
          <div className="flex items-center space-x-3">
            {/* <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-600 rounded-xl flex items-center justify-center"> */}
            <div className="w-8 md:w-10 md:h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                // className="h-5 w-5 md:h-6 md:w-6 text-white"
                className="w-5 md:h-6 md:w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl md:text-3xl font-bold text-white tracking-tight">
                Group Studio
              </h2>
              <p className="text-[8px] md:text-xs text-indigo-400 font-bold uppercase tracking-widest mt-0.5">
                Individual source images for group synthesis
              </p>
            </div>
          </div>
          <button
            onClick={handleCloseOrConfirm}
            className="p-2 md:p-3 hover:bg-white/10 rounded-full transition-all group active:scale-95"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              // className="h-6 w-6 md:h-8 md:w-8 text-slate-400 group-hover:text-white"
              className="w-6 md:h-8 md:w-8 text-slate-400 group-hover:text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-slate-950/20">
          {error && (
            <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl flex items-center space-x-3 text-rose-400 font-bold text-xs uppercase tracking-wider animate-in fade-in slide-in-from-top-2 duration-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="md:h-5 w-5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {members.map((member, idx) => (
              <div
                key={member.id}
                className="space-y-4 animate-in slide-in-from-bottom-4 duration-500"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="bg-slate-900/50 p-4 rounded-3xl border border-white/5 space-y-4 group hover:border-indigo-500/30 transition-all shadow-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                      Source 0{idx + 1}
                    </span>
                    {member.image && (
                      <div
                        className="w-2 rounded-full bg-green-500 animate-pulse"
                        style={{
                          height: "12px",
                          width: "12px",
                          marginRight: "5px"
                        }}
                      ></div>
                    )}
                  </div>

                  <ImageUploader
                    label=""
                    description={`Member ${idx + 1}`}
                    image={member.image}
                    onUpload={(img) => onUpdateMember(idx, { image: img })}
                    // onUpload={(img) => onUploadMemberImage(idx, { image: img })}
                    onClear={() => onUpdateMember(idx, { image: null })}
                    height={220}
                  />

                  <div className="space-y-2 pt-2">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        Identifier
                      </label>
                      <span
                        className={`text-[9px] font-mono ${
                          member.label.length >= 15
                            ? "text-amber-500"
                            : "text-slate-600"
                        }`}
                      >
                        {member.label.length}/15
                      </span>
                    </div>
                    <input
                      type="text"
                      value={member.label}
                      onChange={(e) => {
                        onUpdateMember(idx, {
                          label: e.target.value.slice(0, 15)
                        });
                        if (error && e.target.value.trim() !== "") {
                          setError(null);
                        }
                      }}
                      placeholder="e.g. SUBJECT-1, SUB-2..."
                      className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder:text-slate-700 outline-none focus:border-indigo-500/50 transition-all font-bold tracking-wider"
                    />
                  </div>

                  <div className="space-y-3 pt-2 border-t border-white/5">
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                        Expression
                      </label>
                      <select
                        value={member.expression}
                        onChange={(e) =>
                          onUpdateMember(idx, { expression: e.target.value })
                        }
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-2 py-1.5 text-[10px] text-slate-300 outline-none focus:border-indigo-500/50 transition-all font-bold"
                      >
                        <option value="original">Keep Original</option>
                        <option value="clone">Clone reference</option>
                        <option value="smile">Studio Smile</option>
                        <option value="confident">Confident</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                        Posture
                      </label>
                      <select
                        value={member.posture}
                        onChange={(e) =>
                          onUpdateMember(idx, { posture: e.target.value })
                        }
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-2 py-1.5 text-[10px] text-slate-300 outline-none focus:border-indigo-500/50 transition-all font-bold"
                      >
                        <option value="original">Original</option>
                        <option value="clone">Clone reference</option>
                        <option value="professional">Professional</option>
                        <option value="casual">Casual</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                        Hairstyle
                      </label>
                      <select
                        value={member.hairStyle}
                        onChange={(e) =>
                          onUpdateMember(idx, { hairStyle: e.target.value })
                        }
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-2 py-1.5 text-[10px] text-slate-300 outline-none focus:border-indigo-500/50 transition-all font-bold"
                      >
                        <option value="keep">Keep Original</option>
                        <option value="match-ref">Clone reference</option>
                        <option value="groom">Professional</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                        Beard/Mustache
                      </label>
                      <select
                        value={member.facialHair}
                        onChange={(e) =>
                          onUpdateMember(idx, { facialHair: e.target.value })
                        }
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-2 py-1.5 text-[10px] text-slate-300 outline-none focus:border-indigo-500/50 transition-all font-bold"
                      >
                        <option value="default">
                          No Change / Not Applicable
                        </option>
                        <option value="keep">Keep Original</option>
                        <option value="clone">Clone Reference</option>
                        <option value="shaven">Clean shaven</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                        Eyewear
                      </label>
                      <select
                        value={member.eyewear}
                        onChange={(e) =>
                          onUpdateMember(idx, { eyewear: e.target.value })
                        }
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-2 py-1.5 text-[10px] text-slate-300 outline-none focus:border-indigo-500/50 transition-all font-bold"
                      >
                        <option value="none">Not applicable</option>
                        <option value="keep">Keep Original</option>
                        <option value="clone">Clone Reference</option>
                        <option value="remove">Remove Eyewear</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-indigo-500/5 border border-indigo-500/10 rounded-[2rem] text-center max-w-2xl mx-auto">
            <p className="text-sm text-slate-400 font-medium">
              <span className="text-indigo-400 font-bold uppercase tracking-widest block mb-2 text-xs">
                AI Group Generation Logic
              </span>
              Each source image will be biometrically preserved. Use the labels
              to help the AI identify specific roles or positions when prompting
              for a group portrait.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-6 md:px-10 md:py-8 border-t border-white/5 bg-slate-900/80 flex justify-end">
          <button
            onClick={handleCloseOrConfirm}
            className="w-full md:w-auto px-10 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs tracking-[0.2em] rounded-2xl shadow-xl shadow-indigo-600/20 active:scale-95 transition-all"
          >
            CONFIRM GROUP SETUP
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupUploaderModal;
